import { IssueFieldNames } from 'shared/domain/issueForm/types/fieldNames';
import {
  AttachmentsScope,
  IssueReportType,
  ReportFieldsContent,
} from '../types/model/common';
import {
  isIssueFieldName,
  isReportFieldsContent,
} from '../types/model/reportConfigGuards';
import { IssueReportOnView } from '../types/onView/common';
import { ReportIssueEstimateRow } from './reportIssueEstimateRow';
import {
  readSectionEstimateConfigFromOnView,
  SectionEstimateConfig,
} from './sectionEstimateConfig';

export type { SectionEstimateConfig } from './sectionEstimateConfig';

export type FileMetadataOptions = {
  showFileNames: boolean;
  showFileDescriptions: boolean;
  showActiveLinks: boolean;
  showPhoneNumbers: boolean;
};

export type MessageSectionEstimateConfig = SectionEstimateConfig & {
  attachmentsEnabled: boolean;
};

export type IssueAttachmentsEstimateConfig = SectionEstimateConfig & {
  scope: AttachmentsScope;
};

export type ReportPageEstimateInput = {
  reportKind: IssueReportType;
  issueRows: ReportIssueEstimateRow[];
  issueAttachments: IssueAttachmentsEstimateConfig;
  linkedDocumentation: SectionEstimateConfig;
  events: MessageSectionEstimateConfig;
  incomingEmailMessages: MessageSectionEstimateConfig;
  workflowDecisions: MessageSectionEstimateConfig;
  fieldsContent: ReportFieldsContent;
  selectedFieldNames: IssueFieldNames[];
  getAvailableFieldsForProcess: (processId: string) => Set<IssueFieldNames>;
  fileMetadata: FileMetadataOptions;
  reportMessageChars: number;
  reportMessageInPdf: boolean;
};

/** Stable key of form fields that affect the page estimate (excludes name, recipients, etc.). */
export function getReportPageEstimateFormDependencyKey(
  values: IssueReportOnView
): string {
  const ia = values.issueAttachments;
  const ld = values.linkedDocumentation;
  const ev = values.events;
  const email = values.incomingEmailMessages;
  const wd = values.workflowDecisions;

  return JSON.stringify({
    issueAttachments: {
      enabled: ia.enabled,
      scope: ia.scope._id,
      displayStyle: ia.displayStyle._id,
    },
    linkedDocumentation: {
      enabled: ld.enabled,
      displayStyle: ld.displayStyle._id,
    },
    events: {
      enabled: ev.enabled,
      attachmentsEnabled: ev.attachments.enabled,
      displayStyle: ev.attachments.displayStyle._id,
    },
    incomingEmailMessages: {
      enabled: email.enabled,
      attachmentsEnabled: email.attachments.enabled,
      displayStyle: email.attachments.displayStyle._id,
    },
    workflowDecisions: {
      enabled: wd.enabled,
      attachmentsEnabled: wd.attachments.enabled,
      displayStyle: wd.attachments.displayStyle._id,
    },
    fieldsContent: values.fieldsContent._id,
    selectedFields: values.selectedFields.map((field) => field._id).sort(),
    showFileNames: values.showFileNames,
    showFileDescriptions: values.showFileDescriptions,
    showActiveLinks: values.showActiveLinks,
    showPhoneNumbers: values.showPhoneNumbers,
    reportMessageChars: values.message.trim().length,
    reportMessageInPdf: values.messagePresentation.inPdfReport,
  });
}

export function buildReportPageEstimateInput(
  values: IssueReportOnView,
  issueRows: ReportIssueEstimateRow[],
  reportKind: IssueReportType,
  getAvailableFieldsForProcess: (processId: string) => Set<IssueFieldNames>
): ReportPageEstimateInput {
  const ia = values.issueAttachments;

  return {
    reportKind,
    issueRows,
    issueAttachments: {
      enabled: ia.enabled,
      scope: ia.scope._id,
      displayStyle: readSectionEstimateConfigFromOnView(ia).displayStyle,
    },
    linkedDocumentation: readSectionEstimateConfigFromOnView(
      values.linkedDocumentation
    ),
    events: {
      enabled: values.events.enabled,
      attachmentsEnabled:
        values.events.enabled && values.events.attachments.enabled,
      displayStyle: readSectionEstimateConfigFromOnView(values.events.attachments)
        .displayStyle,
    },
    incomingEmailMessages: {
      enabled: values.incomingEmailMessages.enabled,
      attachmentsEnabled:
        values.incomingEmailMessages.enabled &&
        values.incomingEmailMessages.attachments.enabled,
      displayStyle: readSectionEstimateConfigFromOnView(
        values.incomingEmailMessages.attachments
      ).displayStyle,
    },
    workflowDecisions: {
      enabled: values.workflowDecisions.enabled,
      attachmentsEnabled:
        values.workflowDecisions.enabled &&
        values.workflowDecisions.attachments.enabled,
      displayStyle: readSectionEstimateConfigFromOnView(
        values.workflowDecisions.attachments
      ).displayStyle,
    },
    fieldsContent: isReportFieldsContent(values.fieldsContent._id)
      ? values.fieldsContent._id
      : ReportFieldsContent.all,
    selectedFieldNames: values.selectedFields
      .map((field) => field._id)
      .filter(isIssueFieldName),
    getAvailableFieldsForProcess,
    fileMetadata: {
      showFileNames: values.showFileNames,
      showFileDescriptions: values.showFileDescriptions,
      showActiveLinks: values.showActiveLinks,
      showPhoneNumbers: values.showPhoneNumbers,
    },
    reportMessageChars: values.message.trim().length,
    reportMessageInPdf: values.messagePresentation.inPdfReport,
  };
}
